import { useEffect, useState, type ImgHTMLAttributes } from 'react'
import {
  loadAuthenticatedImageSrc,
  revokeAuthenticatedBlobUrl,
} from '@/lib/authenticated-attachment-image'
import { useAuth } from '@/providers/auth-provider'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

type AuthenticatedImageProps = ImgHTMLAttributes<HTMLImageElement> & {
  rawSrc: string
  loadingClassName?: string
}

export function AuthenticatedImage({
  rawSrc,
  alt,
  className,
  loadingClassName,
  ...imgProps
}: AuthenticatedImageProps) {
  const { session, isLoading: authLoading } = useAuth()
  const [displaySrc, setDisplaySrc] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  const accessToken = session?.access_token

  useEffect(() => {
    let cancelled = false
    let activeBlob: string | null = null

    setFailed(false)
    setDisplaySrc(null)

    if (!rawSrc) return
    if (authLoading) return

    loadAuthenticatedImageSrc(rawSrc)
      .then((url) => {
        if (cancelled) {
          revokeAuthenticatedBlobUrl(url)
          return
        }
        activeBlob = url.startsWith('blob:') ? url : null
        setDisplaySrc(url)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })

    return () => {
      cancelled = true
      revokeAuthenticatedBlobUrl(activeBlob)
    }
  }, [rawSrc, authLoading, accessToken])

  if (failed) {
    return (
      <span className={cn('text-muted-foreground text-xs italic', className)}>
        {alt?.trim() || 'Image unavailable'}
      </span>
    )
  }

  if (!displaySrc) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 text-muted-foreground text-xs',
          loadingClassName,
        )}
        aria-hidden
      >
        <Loader2 className="h-3 w-3 animate-spin" />
      </span>
    )
  }

  return (
    <img
      {...imgProps}
      src={displaySrc}
      alt={alt || ''}
      className={className}
    />
  )
}
